import * as Inovx from '@shared';
import ConfigurationConstants from '../constants/ConfigurationConstants';
import FacilityConstants from '../constants/FacilityConstants';
import SchemaServices from '../services/SchemaServices';
import FacilityStore from '../stores/FacilityStore';
var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
class SchemaActions {
    constructor() {
    }

    async getAllSchemas() {
        const data = await SchemaServices.getAll();
        this._receiveAllSchemas(data);
        return data;
    }

    async createSchema(schema: ISchema, preRefreshCallback?: (newSchema: ISchema) => void) {
        if (!SystemStore.isActionInProgress(ConfigurationConstants.CONFIGURATION_EDIT)) {
            SystemActions.beginAction(ConfigurationConstants.CONFIGURATION_EDIT);
            try {
                const newSchema = await SchemaServices.create(schema);

                if (preRefreshCallback) {
                    preRefreshCallback(newSchema);
                }

                // Refresh the schema list
                await this.getAllSchemas();
            } finally {
                SystemActions.endAction(ConfigurationConstants.CONFIGURATION_EDIT);
            }
        }
    }

    async updateSchema(schema: ISchema) {
        if (!SystemStore.isActionInProgress(ConfigurationConstants.CONFIGURATION_EDIT)) {
            SystemActions.beginAction(ConfigurationConstants.CONFIGURATION_EDIT);
            try {
                await SchemaServices.update(schema);
                await this.getAllSchemas();
            } finally {
                SystemActions.endAction(ConfigurationConstants.CONFIGURATION_EDIT);
            }
        }
    }

    async deleteSchema(schema: ISchema) {
        if (!SystemStore.isActionInProgress(ConfigurationConstants.CONFIGURATION_EDIT)) {
            SystemActions.beginAction(ConfigurationConstants.CONFIGURATION_EDIT);
            try {
                await SchemaServices.deleteSchema(schema);
                await this.getAllSchemas();
            } finally {
                SystemActions.endAction(ConfigurationConstants.CONFIGURATION_EDIT);
            }
        }
    }

    async getAssetSetTaxonomyDetail(schemaID: string) {
        const data = await SchemaServices.getAssetSetTaxonomyDetail(schemaID);
        this._dispatchAction(FacilityConstants.FACILITY_RECEIVE_SCHEMA_ASSET_SET_TAXONOMY, data);
        return data;
    }

    updateAssetSetTaxonomyDetail(data: ILibraryTaxonomyDetail) {
        this._dispatchAction(FacilityConstants.FACILITY_UPDATE_SCHEMA_ASSET_SET_TAXONOMY, data);
    }

    resetAssetSetTaxonomyDetail(data: ILibraryTaxonomyDetail | null) {
        this._dispatchAction(FacilityConstants.FACILITY_RECEIVE_SCHEMA_ASSET_SET_TAXONOMY, data);
    }

    async persistAssetSetTaxonomyDetail(schemaID: string, data?: ILibraryTaxonomyDetail) {
        const taxonomy = data || FacilityStore.getSchemaAssetSetTaxonomyDetail();
        if (!taxonomy?.ID) {
            return null;
        }

        const persistedTaxonomy = await SchemaServices.updateAssetSetTaxonomyDetail(schemaID, taxonomy);
        taxonomy.VersionToken = persistedTaxonomy.VersionToken;
        this._dispatchAction(FacilityConstants.FACILITY_RECEIVE_SCHEMA_ASSET_SET_TAXONOMY, taxonomy);
        return taxonomy;
    }

    async getAssetClassesForUpdate(schemaID: string) {
        const data = await SchemaServices.getAssetClassesUpdateData(schemaID);
        this._receiveAssetClassesUpdateData(data);
        return data;
    }

    async getAnnotationClassesForUpdate(schemaID: string) {
        return await SchemaServices.getAnnotationClassesUpdateData(schemaID);
    }

    addAssetClassToUpdateData(name: string, schemaID: string) {
        const assetClass: IAssetClassUpdateDetail = FacilityStore.getBlankAssetClassUpdateDetail();
        assetClass.Name = name;
        assetClass.SchemaID = schemaID;

        this._dispatchAction(FacilityConstants.FACILITY_UPDATE_ASSET_UPDATE_CLASS, assetClass);
    }

    updateAssetClassInUpdateData(cls: IAssetClassUpdateDetail) {
        this._dispatchAction(FacilityConstants.FACILITY_UPDATE_ASSET_UPDATE_CLASS, cls);
    }

    setConfigurationData(data: any) {
        this._dispatchAction(ConfigurationConstants.CONFIGURATION_SET_DATA, structuredClone(data));
    }
    _finishEdit(close: boolean) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: ConfigurationConstants.CONFIGURATION_FINISH_EDIT,
            modalVisible: !close
        });
    }
    async _resetConfigurationData(selectClass: IAssetClassUpdateDetail = null, classType: 'assetClass' | 'annotationClass' = 'assetClass') {
        let selectedClass = selectClass ?? FacilityStore.getConfiguration();
        if (selectedClass) {
            const itemClasses = classType === 'annotationClass'
                ? await this.getAnnotationClassesForUpdate(selectedClass.SchemaID)
                : await this.getAssetClassesForUpdate(selectedClass.SchemaID);
            if (selectedClass.ID) {
                selectedClass = itemClasses.find(item => item.ID === selectedClass.ID) || itemClasses[0];
            }
            else {
                selectedClass = itemClasses[0];
            }
        }
        
        this.setConfigurationData(selectedClass || null);
    }

    async cancelEdit(classType: 'assetClass' | 'annotationClass' = 'assetClass'): Promise<void> {
        if (FacilityStore.getHasSchemaChanges()) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: async () => {
                    if (classType === 'annotationClass') {
                        await this.createOrUpdateAnnotationClass();
                    }
                    else {
                        await this.createOrUpdateAssetClass();
                    }
                },
                cancelCallback: async () => {
                    this._finishEdit(false);
                    await this._resetConfigurationData(null, classType);
                }
            });
        } else {
            this._finishEdit(false);
            await this._resetConfigurationData(null, classType);
        }
    }

    verifyConfigurationChanges(): boolean {
        if (FacilityStore.getConfigurationModalVisible()) {
            if (FacilityStore.getHasSchemaChanges()) {
                SystemActions.displayInfoMessage({
                    message: InovxLoader.t('facility:message.savePendingChangesInfo')
                });
                return false;
            }
        }
        return true;
    }
    verifyAndCloseConfigurationEdit(): boolean {
        var verified = true;
        if (FacilityStore.getConfigurationModalVisible()) {
            verified = this.verifyConfigurationChanges();
            if (verified) {
                this._finishEdit(true);
            }
        }
        return verified;
    }

    async createOrUpdateAssetClass(assetClass?: IAssetClassUpdateDetail) {
        if (!SystemStore.isActionInProgress(ConfigurationConstants.CONFIGURATION_EDIT)) {
            SystemActions.beginAction(ConfigurationConstants.CONFIGURATION_EDIT);
            try {
                if (assetClass) {
                    this.setConfigurationData(assetClass);
                }

                const data = FacilityStore.getConfiguration();
                // create a deep copy and then remove all temporary IDs
                const dataCopy = structuredClone(data);
                dataCopy.AttributeDefinitions?.forEach(attr => {
                    if (attr.ID && attr.ID.startsWith('temp_')) {
                        attr.ID = null; // remove temporary ID
                    }
                });
                dataCopy.ExternalAttributeDefinitions?.forEach(attr => {
                    if (attr.ID && attr.ID.startsWith('temp_')) {
                        attr.ID = null;
                    }
                });

                if (dataCopy.ID) {
                    await SchemaServices.updateAssetClass(dataCopy);
                }
                else {
                    const result = await SchemaServices.createAssetClass(dataCopy);
                    dataCopy.ID = result.ID; // update the ID with the newly created one
                }

                this._finishEdit(false);
                await this._resetConfigurationData(dataCopy);
            } finally {
                SystemActions.endAction(ConfigurationConstants.CONFIGURATION_EDIT);
            }
        }
    }

    async createOrUpdateAnnotationClass(annotationClass?: IAssetClassUpdateDetail) {
        if (!SystemStore.isActionInProgress(ConfigurationConstants.CONFIGURATION_EDIT)) {
            SystemActions.beginAction(ConfigurationConstants.CONFIGURATION_EDIT);
            try {
                if (annotationClass) {
                    this.setConfigurationData(annotationClass);
                }

                const data = FacilityStore.getConfiguration();
                const dataCopy = structuredClone(data);
                dataCopy.AttributeDefinitions = [];
                dataCopy.ExternalDataSourceLinks = [];
                dataCopy.ExternalAttributeDefinitions = [];
                dataCopy.FriendClasses = [];
                dataCopy.AutoPopulateAssetSets = false;
                dataCopy.AutoPopulateAssetSetClassificationTags = [];
                dataCopy.PrimaryAssetClassID = null;

                if (dataCopy.ID) {
                    await SchemaServices.updateAnnotationClass(dataCopy);
                }
                else {
                    const result = await SchemaServices.createAnnotationClass(dataCopy);
                    dataCopy.ID = result.ID;
                }

                this._finishEdit(false);
                await this._resetConfigurationData(dataCopy, 'annotationClass');
            } finally {
                SystemActions.endAction(ConfigurationConstants.CONFIGURATION_EDIT);
            }
        }
    }

    async deleteAssetClass(assetClass: IAssetClassUpdateDetail) {
        if (!SystemStore.isActionInProgress(ConfigurationConstants.CONFIGURATION_EDIT)) {
            SystemActions.beginAction(ConfigurationConstants.CONFIGURATION_EDIT);
            try {
                try {
                    await SchemaServices.deleteAssetClass(assetClass);
                } catch (error) {
                    if (error.handled === false) {
                        const msg = InovxLoader.t('facility:message.assetClassDeletionFailedBase') + error.message;
                        SystemActions.displayErrorMessage({
                            message: msg
                        });
                        return;
                    }
                    else {
                        throw error;
                    }
                }
                this._finishEdit(false);
                await this._resetConfigurationData(assetClass);
            } finally {
                SystemActions.endAction(ConfigurationConstants.CONFIGURATION_EDIT);
            }
        }
    }

    private _receiveAllSchemas(data: ISchema[]): void {
        this._dispatchAction(FacilityConstants.FACILITY_RECEIVE_SCHEMAS, data);
    }

    private _receiveAssetClassesUpdateData(data: IAssetClassUpdateDetail[]) {
        this._dispatchAction(FacilityConstants.FACILITY_RECEIVE_ASSET_UPDATE_CLASSES, data);
    }

    private _dispatchAction(actionType: string, data?: any) {
        Inovx.Shared.Flux.AppDispatcher.dispatch({
            actionType: actionType,
            data: data
        });
    }
}

const schemaActions = new SchemaActions();

export default schemaActions;
